/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903263772 — Connecting */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useTimer } from '../../../../shared/hooks/useTimer';
import { FeedbackAndReceipt } from '../../../../shared/layouts/FeedbackAndReceipt/FeedbackAndReceipt';
import {
    debug,
    getVmDetails,
    screenLog,
} from '../../../../shared/lib/general';
import { clearSavedTimerEnd, storeOrderState } from '../../../../shared/lib/stateStorage';
import { Order } from '../../../../shared/types';
import { EVENT_NAMES, useSendEventLog } from '../../../event-log';
import { ContentBox, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import vmAnimation from '../../../../shared/ui/AnimatedIcon/animations/vm.json';
import { useGetLoyaltyBalance } from '../../../loyalty';
import {
    MACHINE_ROUTES,
    PAYMENT_ROUTES,
} from '../../../payment/payment.constants';
import { useConnectingHandshake } from '../../hooks/useConnectingHandshake';
import { useConnectingPolling } from '../../hooks/useConnectingPolling';
import { useOrderActions } from '../../hooks/useOrderActions';
import { ORDER_ROUTES } from '../../order.constants';
import styles from './Connecting.module.css';

const Connecting = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { onTimerTimeoutHandler, onError } = useOrderActions();
    const { sendEvent, sendTimeout } = useSendEventLog();
    const vm = useAppStore((s) => s.vm);
    const order = useAppStore((s) => s.order);

    const handleConnectionError = (e: unknown) => {
        sendEvent({ name: EVENT_NAMES.static.CONNECTING_FAILURE });
        onError(e, 'connecting');
    };

    const { isCT5, isPulse, connectTimeoutSec } = getVmDetails(vm);

    const timerHook = useTimer(connectTimeoutSec);

    const handshake = useConnectingHandshake(timerHook);
    const polling = useConnectingPolling({
        orderId: handshake.orderId,
        timerTime: timerHook.time,
        setIsConnected: handshake.setIsConnected,
        onError,
    });

    // Logic
    // 1. set timer to 90 seconds
    // 2. start timer
    // 3. if it is CT10 - short delay and redirect to payment10
    // 4. if CT1 - try to connect to vm
    // 5. if connection is successful - reset timer, hide timer, go to next screen
    // 6. if connection is not successful - go to home screen
    // 7. if timer is 0 - go to home screen

    const handleNavigation = (orderData: Order) => {
        // Pulse machines are CT5 too, but they keep their own amount-entry screens;
        // the isCT5 branch must stay first, or MACHINE_ROUTES would send CT5 snacks
        // (types 1-9) to 'select' and break them.
        const route =
            isCT5 && !isPulse
                ? PAYMENT_ROUTES.paymentCT5
                : MACHINE_ROUTES[vm.type_id] || PAYMENT_ROUTES.select;

        if (!orderData.id) {
            debug('No order ID in navigation link', 'error');
            navigate(generatePath(ORDER_ROUTES.error, { id: 'order' }), {
                replace: true,
            });
            // Without this return the navigation below still runs, on top of the
            // error screen, with id = String(undefined) — a route that literally
            // contains the word "undefined".
            return;
        }

        navigate(generatePath(route, { id: String(orderData.id) }), {
            replace: true,
        });
    };

    const { fetchLoyaltyBalance } = useGetLoyaltyBalance({
        isFetchOnMountEnabled: false,
        onFetchFinished: () => handleNavigation(order),
    });

    // Mount: start timer, attempt ?oid= recovery, fall back to a fresh connect.
    useEffect(() => {
        screenLog('Connecting screen');

        const orderIdFromUrl = new URLSearchParams(window.location.search).get('oid');

        // Fresh entry (no ?oid=) is a new apparatus session, not a browser
        // reload. Clear any stale TIMER_END_TIMESTAMP left by a prior session
        // on the same /:qr/connecting path so useTimer.start() doesn't treat
        // the new session as "already expired" and fire a rogue cancel.
        if (!orderIdFromUrl) {
            clearSavedTimerEnd();
        }

        timerHook.start();
        debug(`try to connect to vm [${vm.vm_id}]`, 'info');

        if (orderIdFromUrl) {
            debug(`Recovery: found ?oid=${orderIdFromUrl}`, 'info');
            handshake.recoverFromOid(orderIdFromUrl).then((recovered) => {
                if (!recovered) {
                    debug('Recovery failed, falling back to fresh connect', 'warn');
                    handshake.connect(handleConnectionError);
                }
            });
        } else {
            handshake.connect(handleConnectionError);
        }

        return () => {
            timerHook.reset();
        };
    }, []);

    // Polling tick: redirect on timeout, otherwise ping while not yet connected.
    // The third case (time > 0 && isConnected) intentionally does nothing —
    // the isConnected effect below handles reset + navigation. Folding it into
    // the timeout branch fires a rogue cancel on the tick that follows
    // setIsConnected(true), since timerHook.reset() bumps time and re-runs
    // this effect with isConnected already true.
    useEffect(() => {
        if (timerHook.time <= 0) {
            debug('timer end - redirect to home', 'info');
            sendTimeout({ name: EVENT_NAMES.timeout.connecting });
            onTimerTimeoutHandler('connecting');
        } else if (!handshake.isConnected) {
            polling.ping();
        }
    }, [timerHook.time]);

    // Once handshake completes, persist order, fetch loyalty, then navigate.
    useEffect(() => {
        if (handshake.isConnected) {
            sendEvent({ name: EVENT_NAMES.static.CONNECTING_SUCCESS });
            timerHook.reset();
            storeOrderState(order);
            debug(`Order ${handshake.orderId} is created`, 'info', order);
            fetchLoyaltyBalance();
        }
    }, [order, handshake.isConnected]);

    return (
        <ScreenWrapper>
            <ScreenTitle>
                {t('Connecting to vm')} {vm?.name} {vm?.vmNumber}
            </ScreenTitle>
            <ContentBox className={styles.animationWrapper}>
                <AnimatedIcon animationData={vmAnimation} className={styles.vm} />

                <FeedbackAndReceipt screenName="connecting" />
            </ContentBox>
        </ScreenWrapper>
    );
};

export { Connecting };
